// Schedule.jsx
import React, { useEffect, useState, useMemo } from "react";
import useCustomFecthQuery from "../../../../Hooks/useCustomFecthQuery";
import Sessions from "./Sessions/Sessions";
import "./swiperStyle.scss";
import Divider from "./Divider";

const Schedule = ({ eventDetails, token, eventID }) => {
  const [groupedSessions, setGroupedSessions] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const {
    data: getMySessions,
    error: errorGetMySessions,
    isLoading: isLoadingGetMySessions,
  } = useCustomFecthQuery(
    ["mySessions", eventID],
    `${process.env.REACT_APP_API}sessions/event/${eventID}`,
    token,
    { enabled: !!eventID }
  );

  // Group sessions by date (memoized for performance)
  const groupSessionsByDate = useMemo(() => (sessions) => {
    const groups = {};
    sessions?.forEach((session) => {
      const sessionDate = new Date(session?.sessionDate);
      const dateKey = sessionDate.toISOString().split("T")[0];
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(session);
    });
    return groups;
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Get sorted unique dates for tabs
  const availableDates = useMemo(() => {
    return Object.keys(groupedSessions).sort((a, b) => new Date(a) - new Date(b));
  }, [groupedSessions]);

  // Calculate which day number (1, 2, 3, etc.) the selected date is
  const getCurrentDayNumber = () => {
    if (!selectedDate || activeTab === "all") return null;
    const dayIndex = availableDates.indexOf(selectedDate);
    return dayIndex + 1; // Day numbers start at 1
  };

  const currentDayNumber = getCurrentDayNumber();

  // Filter sessions based on selected tab
  const filteredSessions = useMemo(() => {
    if (activeTab === "all" || !selectedDate) {
      return getMySessions?.data || [];
    }
    return groupedSessions[selectedDate] || [];
  }, [activeTab, selectedDate, groupedSessions, getMySessions]);

  useEffect(() => {
    if (getMySessions?.data) {
      const grouped = groupSessionsByDate(getMySessions.data);
      setGroupedSessions(grouped);
      
      // Set default selected date to earliest session
      const dates = Object.keys(grouped).sort();
      if (dates.length > 0 && !selectedDate) {
        setSelectedDate(dates[0]);
      }
    }
  }, [getMySessions, groupSessionsByDate, selectedDate]);

  // Loading State - Skeleton UI
  if (isLoadingGetMySessions) {
    return (
      <section className="schedule-container" aria-busy="true">
        <div className="schedule-header">
          <div className="skeleton-title" />
          <Divider />
        </div>
        <div className="sessions-grid">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="session-card skeleton">
              <div className="skeleton-time" />
              <div className="skeleton-content">
                <div className="skeleton-line short" />
                <div className="skeleton-line" />
                <div className="skeleton-line long" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Empty State
  if (getMySessions?.status === "ok" && getMySessions?.data?.length === 0) {
    return (
      <section className="schedule-container">
        <div className="schedule-header">
          <h1 id="schedule" className="schedule-title">Schedule</h1>
          <Divider />
        </div>
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No Sessions Scheduled</h3>
          <p className="empty-subtitle">Check back later for updates on this event's agenda.</p>
        </div>
      </section>
    );
  }

  // Error State
  if (errorGetMySessions) {
    return (
      <section className="schedule-container">
        <div className="schedule-header">
          <h1 id="schedule" className="schedule-title">Schedule</h1>
          <Divider />
        </div>
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>Unable to load sessions. Please try again later.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="schedule-container" aria-labelledby="schedule">
      {/* Header */}
      <header className="schedule-header">
        <h1 id="schedule" className="schedule-title">
          Event Schedule
          <span className="title-underline" aria-hidden="true" />
        </h1>
        <p className="schedule-subtitle">
          {getMySessions?.data?.length} session{getMySessions?.data?.length !== 1 ? 's' : ''} available
        </p>
        <Divider />
      </header>

      {/* Date Filter Tabs */}
      {availableDates.length > 1 && (
        <nav className="date-tabs" aria-label="Filter sessions by date">
          <button
            className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => { setActiveTab("all"); setSelectedDate(""); }}
            aria-selected={activeTab === "all"}
          >
            All Dates
          </button>
          {availableDates.map((date) => (
            <button
              key={date}
              className={`tab-btn ${selectedDate === date ? "active" : ""}`}
              onClick={() => { setActiveTab("date"); setSelectedDate(date); }}
              aria-selected={selectedDate === date}
            >
              {formatDate(date)}
              <span className="tab-badge">{groupedSessions[date]?.length}</span>
            </button>
          ))}
        </nav>
      )}

      {/* Day Indicator */}
      {currentDayNumber && (
        <div className="day-indicator">
          <h3 className="day-label">Day {currentDayNumber}</h3>
          <div className="day-underline" aria-hidden="true" />
        </div>
      )}

      {/* Sessions List */}
      <div className="sessions-wrapper">
        {filteredSessions.length > 0 ? (
          <Sessions 
            sessions={filteredSessions} 
            selectedDate={selectedDate}
            groupedSessions={groupedSessions}
          />
        ) : (
          <div className="no-results">
            <p>No sessions found for the selected date.</p>
            <button 
              className="btn-reset" 
              onClick={() => { setActiveTab("all"); setSelectedDate(""); }}
            >
              View All Sessions
            </button>
          </div>
        )}
      </div>

      {/* Decorative Elements */}
      <div className="schedule-decoration" aria-hidden="true" />
    </section>
  );
};

export default Schedule;